import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api/client';
import { DeviceService } from '../../lib/deviceService/client';
import { useDeviceServiceStatus } from '../../lib/deviceService/status';
import { cmpVersion, parseLibraryVersion, vendorOfGroup } from '../../lib/deviceService/version';
import { pickDeviceServiceBuild, isBehind } from '../../lib/downloads/build32';

interface Dependency { name: string; minVersion?: string; downloadId?: string; note?: string }
interface Download {
  downloadId: string;
  name: string;
  description?: string;
  category: 'device-service' | 'device-service-32' | 'driver' | 'software';
  version: string;
  platform?: 'windows' | 'mac' | 'linux' | 'any';
  deviceGroups?: string[];
  dependencies?: Dependency[];
  fileName: string;
  sizeBytes?: number;
  downloadUrl?: string;
}

/** Scanner most customers use — preselected in the group tabs. */
const PREFERRED_GROUP = 'IB Kojak';

const PLATFORM_LABEL: Record<string, string> = { windows: 'Windows', mac: 'macOS', linux: 'Linux', any: 'All platforms' };

function fmtSize(bytes?: number) {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export function Downloads() {
  const list = useQuery({ queryKey: ['downloads'], queryFn: () => api.get<Download[]>('/downloads') });
  /*
   * `updateAvailable` from the provider is deliberately NOT used here: it compares against the 64-bit
   * catalog entry, so on an i3 workstation it would contradict the badge beside the build being offered.
   * The hero computes its own from `dsShown`.
   */
  const { installed, report } = useDeviceServiceStatus();
  const items = useMemo(() => list.data ?? [], [list.data]);


  const byId = useMemo(() => new Map(items.map((d) => [d.downloadId, d])), [items]);
  const dsLatest = useMemo(
    () => items.filter((d) => d.category === 'device-service').sort((a, b) => cmpVersion(b.version, a.version))[0],
    [items],
  );
  /*
   * The 32-bit build. Deliberately NOT part of `dsLatest`: that takes the highest version in the
   * device-service category, and the two builds are released independently, so a 32-bit release would
   * otherwise become the download every customer is offered.
   */
  const ds32 = useMemo(
    () => items.filter((d) => d.category === 'device-service-32').sort((a, b) => cmpVersion(b.version, a.version))[0],
    [items],
  );
  const groups = useMemo(
    () => Array.from(new Set(items.flatMap((d) => d.deviceGroups ?? []))).sort((a, b) => a.localeCompare(b)),
    [items],
  );
  const [group, setGroup] = useState<string | null>(null);
  // Most customers run the IB Kojak, so it's the default rather than whatever sorts first
  // alphabetically (which was Crossmatch Guardian).
  const activeGroup = group ?? (groups.includes(PREFERRED_GROUP) ? PREFERRED_GROUP : groups[0] ?? null);
  const groupFiles = activeGroup
    ? items.filter((d) => d.category !== 'device-service-32' && (d.deviceGroups ?? []).includes(activeGroup))
    : [];
  /*
   * ONE Device Service, chosen by the selected scanner — the 32-bit build takes the 64-bit one's place
   * rather than appearing beside it. Offering both is worse than offering the wrong one: two services
   * contend for the same port, so a customer who installs everything on the page ends up worse off.
   */
  const { item: dsShown, is32 } = pickDeviceServiceBuild(dsLatest, ds32, activeGroup);
  const dsBehind = isBehind(installed, dsShown?.version, cmpVersion);

  /**
   * Installed device-library version for the selected scanner's vendor — IBScanUltimate for an IB device,
   * and so on. This is what lets a prerequisite say "you have v4.2.1, update to v4.3.0" rather than state
   * a blanket requirement every customer has to interpret. null = service not running, library absent, or
   * version unreadable.
   */
  const [libVersion, setLibVersion] = useState<string | null>(null);

  // Visiting Downloads is itself a "validation" of the Device Service — best-effort probe + report. Also
  // reads the library version for whichever scanner is selected, so switching tabs re-checks.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await DeviceService.connect();
        const v = await s.version();
        if (!cancelled) report(v);
        const vendor = vendorOfGroup(activeGroup);
        if (!vendor) { if (!cancelled) setLibVersion(null); return; }
        // parseLibraryVersion, not the raw value: this endpoint answers with an error sentence when the
        // library is absent, and cmpVersion would read that as 0.0.0 — i.e. "below required".
        const raw = await s.libraryInstalled(vendor);
        if (!cancelled) setLibVersion(parseLibraryVersion(raw));
      } catch { /* DS not running here — fine */ }
    })();
    return () => { cancelled = true; };
  }, [report, activeGroup]);
  /*
   * "Other software" is the everyone-sees-it list, so the 32-bit build is excluded by category and not
   * merely by having device groups. An admin who saves it without groups gets a file nobody is offered,
   * which is the safe direction: the other way round hands a 64-bit workstation an installer that breaks it.
   */
  const otherFiles = items.filter(
    (d) => d.category !== 'device-service' && d.category !== 'device-service-32' && (d.deviceGroups ?? []).length === 0,
  );

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>Downloads</h1>
          <div className="sub">Install the Device Service on each capture workstation, then the drivers &amp; libraries for your scanner.</div>
        </div>
      </div>

      {list.isLoading ? (
        <p className="loading-screen">// loading downloads…</p>
      ) : (
        <>
          {dsShown && (
            /*
             * One hero, and the selected scanner decides which build is in it. The 32-bit service is not an
             * additional download for an i3 DigID — it is THE download, so it takes this place rather than
             * appearing below as a second option.
             */
            <div className={`dl-hero panel${is32 ? ' dl-hero-32' : ''}`}>
              <div className="dl-hero-main">
                <div className="section-title" style={{ marginTop: 0 }}>
                  EFTCreator Device Service{is32 ? ' — 32-bit' : ''}
                </div>
                <p className="sub" style={{ margin: '0 0 8px' }}>
                  {is32
                    ? `Required local service that connects EFTCreator to your fingerprint scanners. The ${activeGroup} needs the 32-bit build — install this one, not the 64-bit version.`
                    : 'Required local service that connects EFTCreator to your fingerprint scanners.'}
                </p>
                <div className="dl-ver-line">
                  <span className="dl-ver-badge">Latest v{dsShown.version}</span>
                  {installed && (dsBehind
                    ? <span className="dl-tag dl-tag-warn">Update available — you have v{installed}</span>
                    : <span className="dl-tag dl-tag-ok">You're up to date (v{installed})</span>)}
                  {!installed && <span className="dl-hint">Open the capture or Device status page to detect your installed version.</span>}
                </div>
                <Deps deps={dsShown.dependencies} byId={byId} onGroup={setGroup} activeGroup={activeGroup} libVersion={libVersion} />
              </div>
              <div className="dl-hero-cta">
                {dsShown.downloadUrl
                  ? <a className={`btn btn-primary${dsBehind ? ' dl-pulse' : ''}`} href={dsShown.downloadUrl} download={dsShown.fileName}>Download v{dsShown.version}</a>
                  : <span className="dl-unavail">Unavailable</span>}
                <div className="dl-cta-sub">{dsShown.platform ? PLATFORM_LABEL[dsShown.platform] ?? dsShown.platform : ''} {fmtSize(dsShown.sizeBytes)}</div>
              </div>
            </div>
          )}

          {groups.length > 0 && (
            <div style={{ marginTop: 26 }}>
              <div className="section-title">Files for your scanner</div>
              <p className="sub" style={{ margin: '0 0 12px' }}>Pick your scanner to see every driver and library it needs.</p>
              <div className="dl-group-tabs">
                {groups.map((g) => (
                  <button key={g} className={`dl-group-tab${g === activeGroup ? ' is-on' : ''}`} onClick={() => setGroup(g)}>{g}</button>
                ))}
              </div>
              <FileTable rows={groupFiles} byId={byId} onGroup={setGroup} activeGroup={activeGroup} emptyLabel="No files listed for this scanner yet." />
            </div>
          )}

          {otherFiles.length > 0 && (
            <div style={{ marginTop: 26 }}>
              <div className="section-title">Other software</div>
              <FileTable rows={otherFiles} byId={byId} onGroup={setGroup} activeGroup={activeGroup} emptyLabel="" />
            </div>
          )}

          {items.length === 0 && (
            <div className="panel empty"><h3>No downloads available</h3><p>Software downloads will appear here.</p></div>
          )}
        </>
      )}
    </div>
  );
}

function FileTable({
  rows, byId, onGroup, emptyLabel, activeGroup,
}: {
  rows: Download[];
  byId: Map<string, Download>;
  onGroup: (g: string) => void;
  emptyLabel: string;
  activeGroup?: string | null | undefined;
}) {
  if (rows.length === 0) return emptyLabel ? <div className="panel empty" style={{ boxShadow: 'none' }}><p>{emptyLabel}</p></div> : null;
  return (
    <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
      <table className="tx-table">
        <thead><tr><th>Name</th><th>Version</th><th>Platform</th><th>Size</th><th></th></tr></thead>
        <tbody>
          {rows.map((d) => (
            <tr key={d.downloadId}>
              <td>
                {d.name}
                {d.description && <div className="acct-code">{d.description}</div>}
                <Deps deps={d.dependencies} byId={byId} onGroup={onGroup} activeGroup={activeGroup} />
              </td>
              <td className="mono">{d.version}</td>
              <td>{d.platform ? PLATFORM_LABEL[d.platform] ?? d.platform : '—'}</td>
              <td>{fmtSize(d.sizeBytes) || '—'}</td>
              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                {d.downloadUrl
                  ? <a className="btn btn-primary btn-sm" href={d.downloadUrl} download={d.fileName}>Download</a>
                  : <span className="dl-unavail">Unavailable</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * A download's prerequisites, scoped to the scanner the operator has selected.
 *
 * These are per-scanner in practice — "IBScanUltimate >= v4.3.0" only concerns customers on an IB
 * device — but it was rendered as a flat requirement of the Device Service itself, so every customer
 * read it as something they had to act on. Worse, it showed "— available: v4.3.0" beside "≥ v4.3.0",
 * which looks like a discrepancy when in fact the requirement is met.
 *
 * The scope is already in the data: the download that satisfies a dependency carries the deviceGroups it
 * belongs to. A dependency with no groups genuinely applies to everyone; otherwise it is shown only when
 * its groups include the selected scanner. Detecting the installed version of a dependency would be
 * better still, but nothing in the Device Service API reports it, so this doesn't pretend to know.
 */
function depGroups(dep: Dependency, byId: Map<string, Download>): string[] {
  return (dep.downloadId ? byId.get(dep.downloadId)?.deviceGroups : undefined) ?? [];
}

function Deps({ deps, byId, onGroup, activeGroup, libVersion }: {
  deps?: Dependency[] | undefined;
  byId: Map<string, Download>;
  onGroup: (g: string) => void;
  /** Selected scanner. Dependencies scoped to other scanners are hidden. */
  activeGroup?: string | null | undefined;
  /** Version of that scanner's device library actually installed here, when known. */
  libVersion?: string | null | undefined;
}) {
  const relevant = (deps ?? []).filter((dep) => {
    const groups = depGroups(dep, byId);
    if (groups.length === 0) return true; // applies to every scanner
    return activeGroup != null && groups.includes(activeGroup);
  });
  if (relevant.length === 0) return null;
  return (
    <div className="dl-deps">
      <span className="dl-deps-label">For your scanner:</span>
      <ul>
        {relevant.map((dep, i) => {
          const target = dep.downloadId ? byId.get(dep.downloadId) : undefined;
          // Only flag a version gap that actually exists — i.e. the catalog can't satisfy the minimum,
          // which is a packaging problem rather than something the customer can fix.
          const behind = target && dep.minVersion && cmpVersion(target.version, dep.minVersion) < 0;
          const firstGroup = target?.deviceGroups?.[0];
          return (
            <li key={i}>
              {/*
                * Three states, deliberately distinct: we know the installed version and it's too old (the
                * only case that demands action), we know it and it's fine (say so, so nobody updates
                * needlessly), or we don't know (state the requirement conditionally rather than implying
                * everyone must act).
                */}
              {dep.minVersion && libVersion && cmpVersion(libVersion, dep.minVersion) < 0 ? (
                <>
                  <strong>{dep.name}</strong> must be updated — you have <span className="mono">v{libVersion}</span>,
                  this Device Service needs <span className="mono">v{dep.minVersion}</span> or newer.
                  <span className="dl-tag dl-tag-warn" style={{ marginLeft: 6 }}>update required</span>
                </>
              ) : dep.minVersion && libVersion ? (
                <>
                  <strong>{dep.name}</strong> <span className="mono">v{libVersion}</span> installed — meets the
                  <span className="mono"> v{dep.minVersion}</span> minimum.
                  <span className="dl-tag dl-tag-ok" style={{ marginLeft: 6 }}>OK</span>
                </>
              ) : dep.minVersion ? (
                <>Needs <strong>{dep.name} v{dep.minVersion}</strong> or newer — update it if you have an older version installed.</>
              ) : (
                <>Needs <strong>{dep.name}</strong>.</>
              )}
              {behind && (
                <span className="dl-tag dl-tag-warn" style={{ marginLeft: 6 }}>
                  catalog has only v{target.version}
                </span>
              )}
              {firstGroup && (
                <button type="button" className="dl-deps-link" onClick={() => onGroup(firstGroup)}>view file</button>
              )}
              {dep.note && <span className="dl-deps-note"> — {dep.note}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
